import { DANGER, PRIMARY } from '../Theme/palette';
import { Divider, Drawer, Typography } from 'antd';
import { EditOutlined, LogoutOutlined, UpOutlined, UserOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '@stores';
import { L } from '@lib';
import { SiderMenu } from './SiderMenu';
import { BigLogo } from './Assets/LogoSvg';
import styled from 'styled-components';

const { Text } = Typography;

export interface IProps {
  visible: boolean;
  onClose: () => void;
}

const MenuWrapper = styled.div`
  .ant-menu {
    background: transparent !important;
    border-inline-end: none !important;
    color: rgba(255, 255, 255, 0.65);
  }

  .ant-menu-item {
    border-radius: 10px;
    margin-inline: 10px !important;
    width: calc(100% - 20px) !important;
    color: rgba(255, 255, 255, 0.85) !important;

    &:hover {
      background: rgba(44, 189, 255, 0.1) !important;
      color: #fff !important;
    }

    .anticon {
      color: ${PRIMARY};
      font-size: 16px;
    }

    &:hover .anticon {
      color: #5ad0ff;
    }
  }

  .ant-menu-item-selected {
    background: linear-gradient(135deg, ${PRIMARY} 0%, #1a9de0 100%) !important;
    color: #fff !important;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(44, 189, 255, 0.3);

    .anticon {
      color: #fff !important;
    }
  }

  .ant-menu-submenu-title {
    border-radius: 10px;
    margin-inline: 10px !important;
    width: calc(100% - 20px) !important;
    color: rgba(255, 255, 255, 0.85) !important;

    &:hover {
      background: rgba(44, 189, 255, 0.1) !important;
      color: #fff !important;
    }

    .anticon {
      color: ${PRIMARY};
    }
  }

  .ant-menu-sub {
    background: transparent !important;
  }
`;

const AccountLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  transition: all 0.15s;
  padding: 8px 0;

  &:hover {
    color: ${PRIMARY};
  }
`;

const LogoutLink = styled(AccountLink)`
  color: ${DANGER.badge};
  &:hover {
    color: ${DANGER.tag};
  }
`;

export const MobileSiderMenu: React.FC<IProps> = ({ visible, onClose }) => {
  const { sessionStore } = useStore();
  const { currentLogin } = sessionStore;
  const [accountOpen, setAccountOpen] = useState(false);
  const initials = `${currentLogin.user?.name?.[0] || ''}${currentLogin.user?.surname?.[0] || ''}`.toUpperCase();

  return (
    <Drawer
      closable={false}
      size={280}
      placement="left"
      open={visible}
      onClose={onClose}
      styles={{
        body: { padding: 0, background: 'linear-gradient(180deg, #0a1628 0%, #132240 100%)' },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ textAlign: 'center', padding: '20px 0 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <BigLogo />
        </div>

        <MenuWrapper style={{ flex: 1, overflow: 'auto', padding: '12px 0' }}>
          <SiderMenu onItemClick={onClose} />
        </MenuWrapper>

        <Divider style={{ margin: '0', borderColor: 'rgba(255,255,255,0.06)' }} />

        <div style={{ padding: '12px 16px 20px' }}>
          <div
            role="button"
            onClick={() => setAccountOpen((o) => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: accountOpen ? 12 : 0, cursor: 'pointer' }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a9de0 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: '#fff',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {initials}
            </div>
            <Text strong ellipsis style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>
              {currentLogin.user?.name} {currentLogin.user?.surname}
            </Text>
            <UpOutlined
              style={{
                marginLeft: 'auto',
                fontSize: 10,
                color: 'rgba(255,255,255,0.3)',
                transform: accountOpen ? 'rotate(0)' : 'rotate(180deg)',
                transition: 'transform 0.2s',
              }}
            />
          </div>
          <div style={{ display: accountOpen ? 'flex' : 'none', flexDirection: 'column', gap: 2, paddingLeft: 44 }}>
            <AccountLink to="/my-account" onClick={onClose}>
              <UserOutlined /> {L('My Account')}
            </AccountLink>
            <AccountLink to="/change-password" onClick={onClose}>
              <EditOutlined /> {L('Change Password')}
            </AccountLink>
            <LogoutLink to="/logout" onClick={onClose}>
              <LogoutOutlined /> {L('Logout')}
            </LogoutLink>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
